/* eslint-disable import/prefer-default-export */
import { useState, useMemo, useCallback } from 'react';
import dayjs from 'shared/helpers/dayjs';

export interface DashboardFilters {
  fromDate: string;
  toDate: string;
  siteIds: string[];
}

interface UseDashboardFiltersReturn {
  filters: DashboardFilters;
  setDateRange: (from: string, to: string) => void;
  setSiteIds: (ids: string[]) => void;
  queryParams: string;
}

function formatDate(date: dayjs.Dayjs): string {
  return date.format('YYYY-MM-DD');
}

export function useDashboardFilters(
  initialFilters?: DashboardFilters
): UseDashboardFiltersReturn {
  const [fromDate, setFromDate] = useState(
    () => initialFilters?.fromDate ?? formatDate(dayjs().subtract(6, 'day'))
  );
  const [toDate, setToDate] = useState(
    () => initialFilters?.toDate ?? formatDate(dayjs())
  );
  const [siteIds, setSiteIds] = useState<string[]>(
    () => initialFilters?.siteIds ?? []
  );

  const setDateRange = useCallback((from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  const filters = useMemo<DashboardFilters>(
    () => ({ fromDate, toDate, siteIds }),
    [fromDate, toDate, siteIds]
  );

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('from_date', fromDate);
    params.set('to_date', toDate);
    if (siteIds.length > 0) {
      params.set('site_ids', siteIds.join(','));
    }
    return params.toString();
  }, [fromDate, toDate, siteIds]);

  return { filters, setDateRange, setSiteIds, queryParams };
}
